import React, { useEffect, useState } from "react";
import {
  Upload,
  Button,
  Card,
  List,
  Typography,
  message,
  Space,
  Spin,
} from "antd";
import { FileText, Image as ImageIcon, UploadCloud, X } from "lucide-react";
import { notificationErroInternalGeneric } from "util/api-utils";
import {
  deleteDocuments,
  getDocuments,
  saveDocuments,
} from "services/managerial/managerial-service";
import { isAdmin } from "constants";

const { Text } = Typography;

export default function DocumentUploadBox({ companyId, isView }) {
  const [fileList, setFileList] = useState([]);
  const [loading, setLoading] = useState(false);
  const urlParam = `${companyId}/document`;

  useEffect(() => {
    if (!companyId) return;

    fetchFiles();
  }, [companyId]);

  const fetchFiles = async () => {
    setLoading(true);
    try {
      const res = await getDocuments(urlParam);
      const files = res.map((file) => ({
        id: file.id,
        name: file.name,
        status: "done",
        url: file.url,
        type: file.type,
      }));
      setFileList(files);
    } catch (err) {
      notificationErroInternalGeneric();
    } finally {
      setLoading(false);
    }
  };

  const handleUpload = async ({ file, onSuccess, onError }) => {
    const formData = new FormData();
    formData.append("file", file);

    setLoading(true);
    try {
      const res = await saveDocuments(urlParam, formData);

      const newFile = {
        id: res.id,
        name: res.name,
        status: "done",
        url: res.url,
        type: res.type,
      };

      setFileList((prev) => [newFile, ...prev]);
      onSuccess();
    } catch (err) {
      message.error("Erro ao enviar arquivo");
      onError && onError(new Error("Upload failed"));
    } finally {
      setLoading(false);
    }
  };

  const handleRemove = async (file) => {
    setLoading(true);
    const urlParamDelete = `${companyId}/${file.id}/document`;
    try {
      await deleteDocuments(urlParamDelete);
      setFileList((prev) => prev.filter((f) => f.id !== file.id));
    } catch (err) {
      message.error("Erro ao remover arquivo");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Spin spinning={loading}>
      <Card size="small" title="Documentos" style={{ marginTop: 20 }}>
        {!isView && isAdmin() && (
          <Upload.Dragger
            customRequest={handleUpload}
            fileList={fileList}
            onRemove={handleRemove}
            multiple
            showUploadList={false}
            style={{ marginBottom: 16 }}
          >
            <Space direction="vertical">
              <UploadCloud size={32} />
              <Text>Arraste arquivos ou clique para selecionar</Text>
              <Text type="secondary">Suporta imagens, PDFs e outros tipos</Text>
            </Space>
          </Upload.Dragger>
        )}
        <div
          style={{ maxHeight: 300, overflowY: "auto" }}
          className="custom-scroll"
        >
          <List
            size="small"
            dataSource={fileList}
            locale={{ emptyText: "Nenhum documento enviado" }}
            renderItem={(file) => (
              <List.Item
                style={{ padding: "6px 0" }}
                actions={
                  !isView
                    ? [
                        <Button
                          type="text"
                          icon={<X size={16} />}
                          onClick={() => handleRemove(file)}
                        />,
                      ]
                    : undefined
                }
              >
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <div style={{ display: "flex", alignItems: "center" }}>
                    {file.type?.startsWith("image/") ? (
                      <ImageIcon size={18} />
                    ) : (
                      <FileText size={18} />
                    )}
                  </div>
                  <div>
                    {file.url ? (
                      <a
                        href={file.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{
                          color: "#1677ff",
                          textDecoration: "underline",
                          fontSize: 14,
                        }}
                      >
                        {file.name}
                      </a>
                    ) : (
                      <span style={{ fontSize: 14 }}>{file.name}</span>
                    )}
                  </div>
                </div>
              </List.Item>
            )}
          />
        </div>
      </Card>
    </Spin>
  );
}
